import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { matchKeywordCount } from './questionScoring';

// PrescriptionAddition lets the student build a brand-new regimen on the
// chart (insulin, VTE prophylaxis, IV fluids, etc.) by adding N rows of
// drug + dose + frequency + route + indication.
//
// Schema:
// {
//   questionType: 'PrescriptionAddition',
//   expectedPrescriptions: [
//     {
//       drugKeywords: ['glargine','lantus','toujeo'],
//       doseKeywords: ['20','units'],
//       frequencyKeywords: ['once','daily','evening'],
//       routeKeywords: ['subcut'],
//       indicationKeywords: ['basal','diabetes'],
//       modelLine: 'Insulin glargine 20 units SC ON for basal cover'
//     }
//   ]
// }

const FIELDS = ['drug', 'dose', 'frequency', 'route', 'indication'];
const KEY_FIELD_MAP = {
  drug: 'drugKeywords',
  dose: 'doseKeywords',
  frequency: 'frequencyKeywords',
  route: 'routeKeywords',
  indication: 'indicationKeywords',
};

const emptyRow = () => ({ drug: '', dose: '', frequency: '', route: '', indication: '' });

// Score one submitted row against one expected row across all five fields.
// Returns { fieldHits: { drug: n, ... }, fieldTargets: { drug: n, ... }, totalHits, totalTargets }.
const scoreRow = (submitted, expected) => {
  const fieldHits = {};
  const fieldTargets = {};
  let totalHits = 0;
  let totalTargets = 0;
  FIELDS.forEach((f) => {
    const kws = expected?.[KEY_FIELD_MAP[f]] || [];
    const hits = matchKeywordCount(submitted?.[f], kws);
    fieldHits[f] = hits;
    fieldTargets[f] = kws.length;
    totalHits += hits;
    totalTargets += kws.length;
  });
  return { fieldHits, fieldTargets, totalHits, totalTargets };
};

// For each expected row, find the best-matching submitted row by drug
// keyword overlap (so the student's row order does not have to match the
// authored order exactly).
const matchExpectedToSubmitted = (expectedList, submittedList) => {
  const used = new Set();
  return expectedList.map((expected) => {
    let bestIdx = -1;
    let bestHits = -1;
    submittedList.forEach((sub, idx) => {
      if (used.has(idx)) return;
      const hits = matchKeywordCount(sub?.drug, expected?.drugKeywords || []);
      if (hits > bestHits) {
        bestHits = hits;
        bestIdx = idx;
      }
    });
    if (bestIdx >= 0 && bestHits > 0) {
      used.add(bestIdx);
      return { submittedIdx: bestIdx, score: scoreRow(submittedList[bestIdx], expected) };
    }
    return { submittedIdx: null, score: scoreRow(emptyRow(), expected) };
  });
};

const PrescriptionAddition = ({ question }) => {
  const expected = useMemo(
    () => (Array.isArray(question?.expectedPrescriptions) ? question.expectedPrescriptions : []),
    [question],
  );
  const [rows, setRows] = useState(() => expected.map(() => emptyRow()));
  const [submitted, setSubmitted] = useState(false);

  const updateField = (idx, field, value) => {
    setRows((curr) => curr.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((curr) => [...curr, emptyRow()]);
  const removeRow = (idx) => setRows((curr) => curr.filter((_, i) => i !== idx));

  const matched = useMemo(() => (submitted ? matchExpectedToSubmitted(expected, rows) : null), [submitted, expected, rows]);

  const overall = useMemo(() => {
    if (!matched) return null;
    let totalHits = 0;
    let totalTargets = 0;
    let rowsCorrect = 0;
    matched.forEach((m) => {
      totalHits += m.score.totalHits;
      totalTargets += m.score.totalTargets;
      // A row counts as correct if at least 75% of its targets are hit
      if (m.score.totalTargets > 0 && m.score.totalHits >= Math.ceil(m.score.totalTargets * 0.75)) {
        rowsCorrect += 1;
      }
    });
    const percentage = totalTargets === 0 ? 0 : Math.round((totalHits / totalTargets) * 100);
    return { totalHits, totalTargets, rowsCorrect, expectedRows: expected.length, percentage };
  }, [matched, expected]);

  const canSubmit = !submitted && rows.some((r) => r.drug.trim().length > 0);

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded prescription-addition-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {submitted && overall ? (
          <Badge bg={overall.percentage >= 75 ? 'success' : overall.percentage >= 50 ? 'warning' : 'danger'} text={overall.percentage >= 75 ? 'light' : 'dark'}>
            {overall.rowsCorrect} / {overall.expectedRows} rows · {overall.percentage}% key elements
          </Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-3">{question.questionText}</p>

      {rows.map((row, idx) => (
        <div key={`row-${idx}`} className="prescription-addition-question__row mb-3 p-2 border rounded bg-white">
          <Row className="g-2">
            <Col xs={12} md={4}>
              <Form.Label className="small mb-1">Drug</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="e.g. Insulin glargine"
                value={row.drug}
                disabled={submitted}
                onChange={(e) => updateField(idx, 'drug', e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Dose</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="e.g. 20 units"
                value={row.dose}
                disabled={submitted}
                onChange={(e) => updateField(idx, 'dose', e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Frequency</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="e.g. ON / TDS"
                value={row.frequency}
                disabled={submitted}
                onChange={(e) => updateField(idx, 'frequency', e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Route</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="e.g. SC / IV"
                value={row.route}
                disabled={submitted}
                onChange={(e) => updateField(idx, 'route', e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Indication</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="e.g. basal"
                value={row.indication}
                disabled={submitted}
                onChange={(e) => updateField(idx, 'indication', e.target.value)}
              />
            </Col>
          </Row>
          {!submitted && rows.length > 1 ? (
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              className="mt-2"
              onClick={() => removeRow(idx)}
            >
              Remove row
            </Button>
          ) : null}
        </div>
      ))}

      {!submitted ? (
        <div className="d-flex gap-2 mt-3">
          <Button type="button" variant="outline-secondary" size="sm" onClick={addRow}>+ Add row</Button>
          <Button type="button" variant="primary" disabled={!canSubmit} onClick={() => setSubmitted(true)}>
            Submit regimen
          </Button>
        </div>
      ) : null}

      {submitted && matched ? (
        <div className="mt-4">
          <h5>Per-row feedback</h5>
          {matched.map((m, idx) => {
            const exp = expected[idx];
            const rowFound = m.submittedIdx !== null;
            const score = m.score;
            return (
              <div key={`fb-${idx}`} className="prescription-addition-question__feedback p-2 mb-2 border rounded">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <strong>Expected:</strong> <span className="text-muted">{exp?.modelLine || (exp?.drugKeywords || []).join(' / ')}</span>
                  <Badge bg={rowFound ? (score.totalHits >= Math.ceil(score.totalTargets * 0.75) ? 'success' : 'warning') : 'danger'} text="light">
                    {score.totalHits} / {score.totalTargets} key elements
                  </Badge>
                </div>
                {!rowFound ? (
                  <div className="small text-danger mt-1">No matching row submitted.</div>
                ) : (
                  <div className="small text-muted mt-1">
                    Matched submitted row {m.submittedIdx + 1}.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </Container>
  );
};

export default PrescriptionAddition;
